<ion-header [translucent]="true">
  <ion-toolbar>
    <ion-buttons slot="start">
      <ion-back-button [text]="'ZURUECK' | translate"></ion-back-button>
    </ion-buttons>
    <ion-title>{{ 'PROJEKTE' | translate }}</ion-title>
    <ion-buttons slot="end">
      <ion-menu-button></ion-menu-button>
    </ion-buttons>
  </ion-toolbar>
</ion-header>

<ion-content>
  <div class="padding-16">
    <div class="flex flex-column gap-16">
      @if (project(); as project) {
        <work-ui-badge
          [text]="project?.status?.name || '-'"
          [variant]="project.statusId | getLabelVariantForStatus"
          size="small" />
        <h1>{{ project.numberQualified }}</h1>
        @if (activeAssignment(); as activeAssignment) {
          <work-ui-badge [text]="activeAssignment.label" size="large" variant="warning" />
        }
        @if (project.container?.contact?.montageNotes; as montageNotes) {
          <div class="work-card work-card--warning flex flex-row gap-12">
            <work-ui-icon name="warning" />
            <div class="flex flex-column gap-4">
              <h3>{{ 'HINWEISE_MONTAGETEAM' | translate }}</h3>
              <p [innerHTML]="montageNotes | newlineToHtmlLinebreak | linkifyText"></p>
            </div>
          </div>
        }
        <div class="work-card flex flex-row space-between">
          <div>
            <h2>{{ 'ADRESSE' | translate }}</h2>
            <p>
              {{ project.objectStreet }}<br>
              {{ project.objectZip }} {{ project.objectCity }} ({{ project.objectCanton }})
            </p>
          </div>
          <div>
            <work-ui-button (click)="openGoogleMaps()" [iconOnly]="true" iconName="map" />
          </div>
        </div>

        @if (project.tobNotes || project.salesinformation) {
          <div class="work-card flex flex-column gap-8">
            <h2>{{ 'NOTIZEN' | translate }}</h2>
            @if (project.tobNotes) {
              <p [innerHTML]="project.tobNotes | newlineToHtmlLinebreak | linkifyText"></p>
            }
            @if (project.salesinformation) {
              <p [innerHTML]="project.salesinformation | newlineToHtmlLinebreak | linkifyText"></p>
            }
          </div>
        }

        <div class="work-card flex flex-column gap-16">
          <h2>{{ 'ANSPRECHPERSONEN' | translate }}</h2>
          @if (project.user; as user) {
            <work-ui-infoblock
              [avatarImgSrc]="user.userImage ? (user.userImage.fileSrcThumbnail | prependServerToFileSrc) : null"
              [clickable]="true"
              [label]="'KUNDENBERATUNG' | translate"
              [routerLink]="['/mitarbeitende/adressbuch/details/', user.id]"
              [subtitle]="user.phone"
              [textCenteredVertically]="true"
              [title]="user.firstName! + ' ' + user.lastName! || ''"
              [withAvatar]="true"
              avatarStatus="none" />
          }
          @if (project.tpbUser; as user) {
            <work-ui-infoblock
              [avatarImgSrc]="user.userImage ? (user.userImage.fileSrcThumbnail | prependServerToFileSrc) : null"
              [clickable]="true"
              [label]="'PROJEKTLEITUNG' | translate"
              [routerLink]="['/mitarbeitende/adressbuch/details/', user.id]"
              [subtitle]="user.phone"
              [textCenteredVertically]="true"
              [title]="user.firstName! + ' ' + user.lastName! || ''"
              [withAvatar]="true"
              avatarStatus="none" />
          }
          @if (project.externalProjectManagerContact; as externalProjectManagerContact) {
            <hr>
            <div class="flex flex-column gap-4">
              <h3>{{ 'PROJEKTLEITER_EXTERN' | translate }} ({{ project?.container?.contact?.company }})</h3>
              <p>
                <strong>{{ externalProjectManagerContact | getFullnameOfContact:true }}</strong>
              </p>
              @for (phone of externalProjectManagerContact.phones; track phone.id) {
                <a [href]="phone.value | telHref">{{ phone.value }}</a>
              }
              @for (mail of externalProjectManagerContact.mails; track mail.id) {
                <a [href]="mail.value">{{ mail.value }}</a>
              }
            </div>
          }
        </div>
      }

      @if (assignments().length > 0) {
        <div class="work-card">
          <work-ui-accordion-item [isOpen]="false" [noPadding]="true" [withBorder]="false">
            <h2 accordion-header>{{ 'EINSAETZE' | translate }}</h2>
            <div accordion-content class="flex flex-column gap-16 margin-top-16">
              @for (assignment of assignments(); track assignment.task.id) {
                <div class="flex flex-column gap-8 work-card padding-narrow">
                  <work-ui-badge
                    [text]="assignment.label"
                    [variant]="assignment === activeAssignment() ? 'warning' : 'neutral'"
                    size="small" />
                  <h4>{{ assignment.task.start | formatDaterangeWithSameDayCheck:(assignment.task.end ?? assignment.task.start) }}</h4>
                  @if (assignment.task.name) {
                    <p><small>{{ assignment.task.name }}</small></p>
                  }
                </div>
              }
            </div>
          </work-ui-accordion-item>
        </div>
      }

      @if (canReadCalculation() && project()?.calculation; as calculation) {
        <div class="work-card">
          <work-ui-accordion-item [isOpen]="false" [noPadding]="true" [withBorder]="false">
            <h2 accordion-header>{{ 'KALKULATION' | translate }}</h2>
            <div accordion-content class="flex flex-column gap-16 margin-top-16">
              @for (group of calculationitemsGroups(); track group.id) {
                <div class="flex flex-column gap-8">
                  <div class="flex flex-row space-between gap-8">
                    <h3>{{ group.name }}</h3>
                    <h3 class="work-text-no-wrap">{{ group.saleChfTotalExcl | formatNumber:2 }}</h3>
                  </div>
                  @for (calculationitem of group.calculationitems; track calculationitem.id) {
                    <div class="flex flex-row space-between gap-8">
                      <div class="flex flex-column">
                        <p>{{ calculationitem.item?.name ?? calculationitem.itemDescription }}</p>
                        <small class="work-text-color-light">
                          {{ calculationitem.targetAmount | formatNumber }} {{ calculationitem.unit?.name }}
                          @if (calculationitem.optional) {
                            · {{ 'OPTIONAL' | translate }}
                          }
                        </small>
                      </div>
                      <p class="work-text-no-wrap">{{ calculationitem.saleChfTotalExcl | formatNumber:2 }}</p>
                    </div>
                  } @empty {
                    <p><small class="work-text-color-light">{{ 'KEINE_POSITIONEN_IN_GRUPPE' | translate }}</small></p>
                  }
                </div>
                <hr>
              }
              <div class="flex flex-column gap-4">
                <div class="flex flex-row space-between gap-8">
                  <p>{{ 'TOTAL_EXKL_MWST' | translate }}</p>
                  <p class="work-text-no-wrap">CHF {{ calculation.saleChfTotalExcl | formatNumber:2 }}</p>
                </div>
                <div class="flex flex-row space-between gap-8">
                  <p><b>{{ 'TOTAL_INKL_MWST_UND_RABATT' | translate }}</b></p>
                  <p class="work-text-no-wrap"><b>CHF {{ calculation.saleChfTotalInclWithDiscount | formatNumber:2 }}</b></p>
                </div>
              </div>
            </div>
          </work-ui-accordion-item>
        </div>
      }

      @if (tasks().length > 0) {
        <div class="work-card flex flex-column gap-16">
          <h2>{{ 'AUFGABEN' | translate }}</h2>
          @for (task of tasks(); track task.id) {
            <div class="flex flex-row space-between vertical-center work-card padding-narrow"
                 [routerLink]="!task.end && task.standardtaskId === 18 ? ['erstinstruktion'] : !task.end && task.standardtaskId === 6 ? ['abschlussprotokoll'] : null">
              <div class="flex flex-column gap-8">
                @if (task.end) {
                  <work-ui-badge text="ERLEDIGT" variant="success" size="small" />
                } @else {
                  <work-ui-badge text="OFFEN" variant="warning" size="small" />
                }
                <h4>{{ task.name ?? 'Task' }}</h4>
                <p><small>{{ task.user?.firstName }} {{ task.user?.lastName }}</small></p>
              </div>
              @if (!task.end) {
                <div>
                  <work-ui-icon name="arrow_forward" />
                </div>
              }
            </div>
          }
        </div>
      }

      <div class="work-card flex flex-column gap-16">
        <work-ui-button text="DOKUMENTE" [fullWidth]="true" iconName="note_stack"
                        variant="secondary"
                        routerLink="dokumente"
                        routerDirection="forward" />
        <work-ui-button text="FOTOS" [fullWidth]="true" iconName="photo_camera"
                        variant="secondary"
                        routerLink="fotos"
                        routerDirection="forward" />
        <work-ui-button text="SCHADEN_MELDEN" [fullWidth]="true" iconName="warning"
                        variant="secondary"
                        routerLink="schaden-melden"
                        routerDirection="forward" />
      </div>
    </div>
  </div>
</ion-content>
